import axios from 'axios';

// Admin auth for the web panel — part of the API security hardening plan
// (see SECURITY_HARDENING_PLAN.md). Two global axios interceptors:
//
//  1. REQUEST: attach the admin access token to every /api/admin/* call (only
//     those, only when no Authorization header is already set). Never changes a
//     request's URL/method/body/query, never touches non-admin calls.
//
//  2. RESPONSE: on a 401/403 from an admin call, silently mint a new access
//     token from the stored refresh token (/api/admin/refresh) and retry the
//     original request once. If refresh fails (no/expired refresh token), clear
//     the admin session and bounce to the admin login. This makes backend admin
//     auth enforcement safe: an expired access token becomes a seamless renewal
//     for a live admin, or a clean re-login — never broken pages.
//
// Every admin page uses this one global axios and none use fetch() (guarded by
// scripts/check_admin_axios.sh), so these interceptors cover 100% of admin calls
// with zero call-site changes. Registered at module load (imported from
// index.js) before React renders.

const ADMIN_TOKEN_KEY = 'shootzu_admin_token';
const ADMIN_REFRESH_KEY = 'shootzu_admin_refresh_token';

let registered = false;
let refreshPromise = null; // shared in-flight refresh so concurrent 401s renew once

function backendBase() {
  return process.env.REACT_APP_BACKEND_URL || '';
}

function setAuthHeader(config, token) {
  const headers = config.headers || {};
  if (typeof headers.set === 'function') headers.set('Authorization', `Bearer ${token}`);
  else headers.Authorization = `Bearer ${token}`;
  config.headers = headers;
}

function clearAdminSession() {
  localStorage.removeItem(ADMIN_TOKEN_KEY);
  localStorage.removeItem(ADMIN_REFRESH_KEY);
  localStorage.removeItem('shootzu_admin');
  localStorage.removeItem('shootzu_admin_email');
}

function redirectToAdminLogin() {
  // Login lives at /szadmin — avoid a redirect loop if we're already there.
  if (window.location.pathname !== '/szadmin') {
    window.location.href = '/szadmin?expired=true';
  }
}

async function refreshAdminAccessToken() {
  const refresh = localStorage.getItem(ADMIN_REFRESH_KEY);
  if (!refresh) throw new Error('no admin refresh token');
  // Bare axios call so the request interceptor's token-attach is harmless and
  // the response interceptor skips it (isAuthEndpoint), avoiding recursion.
  const resp = await axios.post(`${backendBase()}/api/admin/refresh`, { refresh_token: refresh });
  const newToken = resp.data && resp.data.token;
  if (!newToken) throw new Error('admin refresh returned no token');
  localStorage.setItem(ADMIN_TOKEN_KEY, newToken);
  if (resp.data.refresh_token) localStorage.setItem(ADMIN_REFRESH_KEY, resp.data.refresh_token);
  return newToken;
}

export function installAdminAuthInterceptor() {
  if (registered) return; // idempotent: safe under StrictMode / repeated imports
  registered = true;

  axios.interceptors.request.use((config) => {
    try {
      const url = config.url || '';
      if (url.includes('/api/admin/')) {
        const token = localStorage.getItem(ADMIN_TOKEN_KEY);
        if (token) {
          const headers = config.headers || {};
          const existing =
            headers.Authorization ||
            headers.authorization ||
            (typeof headers.get === 'function' && headers.get('Authorization'));
          if (!existing) setAuthHeader(config, token);
        }
      }
    } catch (_) {
      // An interceptor must never throw — pass the request through unchanged.
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config = error.config || {};
      const url = config.url || '';
      const status = error.response && error.response.status;
      const isAdmin = url.includes('/api/admin/');
      const isAuthEndpoint =
        url.includes('/api/admin/login') || url.includes('/api/admin/refresh');

      // Only 401 (token missing/expired/invalid) triggers a silent refresh. A 403
      // means the token is valid but the action is forbidden — refreshing can't
      // help and could mask a genuine business-logic 403, so we leave it alone.
      if (isAdmin && !isAuthEndpoint && status === 401 && !config.__adminRetried) {
        // Never send someone who isn't in an admin session to the admin login. A business
        // owner whose screen still called an /api/admin/ endpoint got bounced to /szadmin,
        // where their real password was "incorrect" (SHOOTZU_BIBLE §15 2026-10-16). With no
        // admin tokens at all, let the call fail like any other; AdminLayout already
        // redirects admin pages synchronously when both tokens are missing.
        if (!localStorage.getItem(ADMIN_TOKEN_KEY) && !localStorage.getItem(ADMIN_REFRESH_KEY)) {
          return Promise.reject(error);
        }
        config.__adminRetried = true;
        try {
          if (!refreshPromise) {
            refreshPromise = refreshAdminAccessToken().finally(() => { refreshPromise = null; });
          }
          const newToken = await refreshPromise;
          setAuthHeader(config, newToken); // retry with the fresh token
          return axios(config);
        } catch (_) {
          clearAdminSession();
          redirectToAdminLogin();
          return Promise.reject(error);
        }
      }
      return Promise.reject(error);
    }
  );
}

installAdminAuthInterceptor();
